import { MapPin, UserRound, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandGroup,
    CommandItem,
    CommandSeparator,
} from '@apps/web/components/ui/command';
import { useClientOptions } from '@apps/web/features/coverage/api/client_options';
import { type useCoverageOpenShiftClients } from '@apps/web/features/coverage/api/open_shift_clients';
import { requestErrorMessage } from '@apps/web/features/coverage/model/request_model';
import { type PotentialClientPin } from '@apps/web/features/coverage/ui/map_selection_card';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { getApiClient } from '@apps/web/lib/api_client';
import { cn } from '@apps/web/lib/utils';
import {
    type WorkforceMapClientPoint,
    type WorkforceMapPoint,
} from '@libraries/typescript/api_client';

export function CoverageMapSearch({
    organizationId,
    caregiverSearch,
    selectedLocation,
    clientPoints,
    caregiverPoints,
    isLoadingClientLocations,
    clientsLayerEnabled,
    clientLocationsFailed = false,
    onRetryClientLocations,
    openShiftClients,
    onPlotted,
    onSelectCaregiver,
    onClear,
    clearEpoch,
}: {
    readonly organizationId: string | null;
    readonly caregiverSearch: string;
    readonly selectedLocation: PotentialClientPin | null;
    readonly clientPoints: ReadonlyArray<WorkforceMapClientPoint>;
    readonly caregiverPoints: ReadonlyArray<WorkforceMapPoint>;
    readonly isLoadingClientLocations: boolean;
    readonly clientsLayerEnabled: boolean;
    readonly clientLocationsFailed?: boolean;
    readonly onRetryClientLocations?: () => void;
    readonly openShiftClients?: ReturnType<typeof useCoverageOpenShiftClients>;
    readonly onPlotted: (pin: PotentialClientPin) => void;
    readonly onSelectCaregiver: (point: WorkforceMapPoint) => void;
    readonly onClear: () => void;
    readonly clearEpoch: number;
}) {
    const { formatName } = useFormatPersonName();
    const [query, setQuery] = useState(caregiverSearch);
    const lastCaregiverSearchRef = useRef(caregiverSearch);
    const lastClearEpochRef = useRef(clearEpoch);
    const [isPlotting, setIsPlotting] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const activeLookupRef = useRef<symbol | null>(null);

    useEffect(
        () => () => {
            activeLookupRef.current = null;
        },
        [],
    );
    useEffect(() => {
        if (caregiverSearch === lastCaregiverSearchRef.current) {
            return;
        }
        lastCaregiverSearchRef.current = caregiverSearch;
        activeLookupRef.current = null;
        setQuery(caregiverSearch);
        setMessage(null);
        setIsPlotting(false);
    }, [caregiverSearch]);
    useEffect(() => {
        if (clearEpoch === lastClearEpochRef.current) {
            return;
        }
        lastClearEpochRef.current = clearEpoch;
        activeLookupRef.current = null;
        setQuery('');
        setMessage(null);
        setIsPlotting(false);
        lastCaregiverSearchRef.current = '';
    }, [clearEpoch]);

    const plotClient = (client: { id: string; name: string }) => {
        const point = clientPoints.find(
            (point) => point.client_id === client.id,
        );
        setMessage(null);
        if (!clientsLayerEnabled) {
            setMessage(
                'Client mapping is unavailable. Search a full street address to plot a location.',
            );
            return;
        }
        if (clientLocationsFailed) {
            setMessage(
                'Client locations couldn’t be loaded. Retry client locations, then select the client again.',
            );
            return;
        }
        if (!point) {
            setMessage(
                `Location unavailable for ${formatName(client.name)}. Their address may be incomplete or not yet mapped. Check the address in their client profile, or search a full street address here.`,
            );
            return;
        }
        lastCaregiverSearchRef.current = '';
        onPlotted({
            latitude: point.latitude,
            longitude: point.longitude,
            clientId: client.id,
            clientName: client.name,
        });
    };

    const plotAddress = async () => {
        const trimmedQuery = query.trim();
        if (!organizationId || trimmedQuery.length < 3 || isPlotting) {
            return;
        }
        const requestToken = Symbol('potential-client-geocode');
        activeLookupRef.current = requestToken;
        setIsPlotting(true);
        setMessage(null);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/coverage/map/potential-client-geocode',
                pathParams: { organization_id: organizationId },
                body: { address: trimmedQuery },
                reason: 'coverage-potential-client',
                idempotent: true,
            });
            if (activeLookupRef.current !== requestToken) {
                return;
            }
            if (response.result) {
                lastCaregiverSearchRef.current = '';
                onPlotted({
                    latitude: response.result.latitude,
                    longitude: response.result.longitude,
                    formattedAddress: response.result.formatted_address,
                });
            } else {
                setMessage(
                    'We couldn’t find that address. Try adding a city and state.',
                );
            }
        } catch (requestError) {
            if (activeLookupRef.current === requestToken) {
                setMessage(
                    requestErrorMessage(
                        requestError,
                        'We couldn’t look up that address.',
                    ),
                );
            }
        } finally {
            if (activeLookupRef.current === requestToken) {
                activeLookupRef.current = null;
                setIsPlotting(false);
            }
        }
    };

    const clear = () => {
        lastCaregiverSearchRef.current = '';
        activeLookupRef.current = null;
        setQuery('');
        setMessage(null);
        setIsPlotting(false);
        onClear();
    };

    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!selectedLocation?.clientId) {
            return;
        }
        activeLookupRef.current = null;
        lastCaregiverSearchRef.current = '';
        setQuery(selectedLocation.clientName ?? '');
        setMessage(null);
        setIsPlotting(false);
        setOpen(false);
    }, [selectedLocation]);
    const clientOptions = useClientOptions(
        !openShiftClients && (open || query !== '') ? organizationId : null,
        query.trim(),
    );
    const matchingClients = openShiftClients
        ? openShiftClients.clients.filter((client) =>
              formatName(client.name)
                  .toLocaleLowerCase()
                  .includes(query.trim().toLocaleLowerCase()),
          )
        : clientOptions.clients;
    const isLoading = openShiftClients?.isLoading ?? clientOptions.isLoading;
    const error = openShiftClients
        ? openShiftClients.error
        : clientOptions.error;
    const [clientLimit, setClientLimit] = useState(25);
    const filteringOpenClients = !!openShiftClients;
    useEffect(() => setClientLimit(25), [query, filteringOpenClients]);
    const clients = openShiftClients
        ? matchingClients.slice(0, clientLimit)
        : matchingClients;
    const hasMore = openShiftClients
        ? matchingClients.length > clientLimit
        : clientOptions.hasMore;
    const loadMore = openShiftClients
        ? () => setClientLimit((count) => count + 25)
        : clientOptions.loadMore;
    const isValidating = openShiftClients
        ? openShiftClients.isLoading
        : clientOptions.isValidating;
    const retryClients = openShiftClients
        ? openShiftClients.retry
        : clientOptions.mutate;
    const [activeOption, setActiveOption] = useState('');
    const matchingCaregivers = caregiverPoints.filter((point) =>
        point.name
            .toLocaleLowerCase()
            .includes(query.trim().toLocaleLowerCase()),
    );
    const [caregiverLimit, setCaregiverLimit] = useState(25);
    useEffect(() => setCaregiverLimit(25), [query]);
    const firstClientId = clients[0]?.id;
    const firstCaregiverId = matchingCaregivers[0]?.caregiver_id;
    useEffect(() => {
        setActiveOption(
            firstCaregiverId
                ? `caregiver:${firstCaregiverId}`
                : (firstClientId ?? 'plot-address'),
        );
    }, [firstClientId, firstCaregiverId, query]);
    const selectClient = (clientId: string, name: string) => {
        setOpen(false);
        setQuery(name);
        activeLookupRef.current = null;
        setIsPlotting(false);
        plotClient({ id: clientId, name });
    };
    const submitAddress = () => {
        setOpen(false);
        void plotAddress();
    };

    return (
        <div className="w-full min-w-0 sm:w-96">
            <div className="relative">
                <Combobox
                    open={open}
                    onOpenChange={setOpen}
                    displayValue={query || undefined}
                    placeholder="Search caregivers, clients or addresses"
                    searchPlaceholder="Name, street, city, or ZIP"
                    searchValue={query}
                    onSearchValueChange={(value) => {
                        setQuery(value);
                    }}
                    clearSearchOnClose={false}
                    disabled={!organizationId || isPlotting}
                    triggerProps={{
                        'aria-label': 'Search caregivers, clients or addresses',
                    }}
                    showCaret={!query}
                    triggerClassName={cn('h-9 min-w-0', query && 'pr-10')}
                    contentClassName="w-96 max-w-[calc(100vw-2rem)]"
                    matchTriggerWidth={false}
                    commandProps={{
                        shouldFilter: false,
                        value: activeOption,
                        onValueChange: setActiveOption,
                    }}
                >
                    {matchingCaregivers.length > 0 ? (
                        <CommandGroup heading="Caregivers">
                            {matchingCaregivers
                                .slice(0, caregiverLimit)
                                .map((point) => (
                                    <CommandItem
                                        key={point.caregiver_id}
                                        value={`caregiver:${point.caregiver_id}`}
                                        onSelect={() => {
                                            activeLookupRef.current = null;
                                            setIsPlotting(false);
                                            setMessage(null);
                                            setOpen(false);
                                            setQuery(point.name);
                                            lastCaregiverSearchRef.current =
                                                point.name;
                                            onSelectCaregiver(point);
                                        }}
                                    >
                                        <UserRound
                                            className="size-3.5"
                                            aria-hidden={true}
                                        />
                                        {formatName(point.name)}
                                    </CommandItem>
                                ))}
                            {matchingCaregivers.length > caregiverLimit ? (
                                <CommandItem
                                    value="more-caregivers"
                                    onSelect={() =>
                                        setCaregiverLimit((count) => count + 25)
                                    }
                                >
                                    Show more caregivers
                                </CommandItem>
                            ) : null}
                        </CommandGroup>
                    ) : null}
                    <CommandGroup heading="Clients">
                        {isLoadingClientLocations && clients.length > 0 ? (
                            <p
                                className="text-muted-foreground px-2 py-3 text-xs"
                                role="status"
                            >
                                Loading client locations…
                            </p>
                        ) : null}
                        {isLoading ? (
                            <p
                                className="text-muted-foreground px-2 py-3 text-xs"
                                role="status"
                            >
                                Loading clients…
                            </p>
                        ) : error ? (
                            <div className="px-2 py-2 text-xs" role="alert">
                                Couldn’t load clients.
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => void retryClients()}
                                >
                                    Try again
                                </Button>
                            </div>
                        ) : clients.length === 0 ? (
                            <p
                                className="text-muted-foreground px-2 py-3 text-xs"
                                role="status"
                            >
                                {query.trim().length > 0 &&
                                query.trim().length < 3
                                    ? 'Type at least 3 characters to search.'
                                    : openShiftClients
                                      ? 'No matching clients with open shifts in the next 30 days.'
                                      : 'No matching clients.'}
                            </p>
                        ) : (
                            clients.map((client) => {
                                const name = client.name;
                                return (
                                    <CommandItem
                                        key={client.id}
                                        value={client.id}
                                        disabled={isLoadingClientLocations}
                                        onSelect={() =>
                                            selectClient(client.id, name)
                                        }
                                    >
                                        <UserRound
                                            className="size-3.5"
                                            aria-hidden="true"
                                        />
                                        {formatName(name)}
                                    </CommandItem>
                                );
                            })
                        )}
                        {hasMore ? (
                            <CommandItem
                                value="more-clients"
                                disabled={isValidating}
                                onSelect={loadMore}
                            >
                                Show more clients
                            </CommandItem>
                        ) : null}
                    </CommandGroup>
                    {query.trim().length >= 3 ? (
                        <Fragment>
                            <CommandSeparator />
                            <CommandGroup heading="Address">
                                <CommandItem
                                    value="plot-address"
                                    onSelect={submitAddress}
                                >
                                    <MapPin
                                        className="size-3.5"
                                        aria-hidden="true"
                                    />
                                    Plot address: {query.trim()}
                                </CommandItem>
                            </CommandGroup>
                        </Fragment>
                    ) : null}
                </Combobox>
                {query ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
                        aria-label="Clear map search"
                        onClick={clear}
                    >
                        <X className="size-4" />
                    </Button>
                ) : null}
            </div>
            {message ? (
                <div className="text-destructive mt-1 text-xs" role="alert">
                    <p>{message}</p>
                    {clientLocationsFailed ? (
                        <Button
                            variant="link"
                            size="sm"
                            onClick={onRetryClientLocations}
                        >
                            Retry client locations
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
